import type { OrderRequest } from '@/legacy-api/orders-slice.gen'

import { formatPrice, formatTime } from '@/utils/presentation/formatting'

/**
 * Formats an execution timestamp to display time in HH:mm:ss format.
 * Returns a placeholder if the timestamp is null or invalid.
 *
 * @param timestamp - The ISO string timestamp to format
 * @returns The formatted time string (HH:mm:ss) or placeholder
 */
export function formatExecutionTimestamp(timestamp: string | null): string {
  return formatTime(timestamp, {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/**
 * Formats a price for order and execution displays with a minimum of 4 decimal places.
 * Shows trailing zeros to ensure at least 4 decimal places, and preserves additional
 * precision up to 8 decimal places when present in the input value.
 *
 * @param price - The price to format, which can be a number or null/undefined
 * @returns The formatted price string with minimum 4 decimal places
 */
export function formatOrderPrice(price: number | null | undefined): string {
  if (price === null || price === undefined) {
    return formatPrice(price)
  }

  // Determine how many decimal places the input has
  const decimalPlaces = price.toString().split('.')[1]?.length || 0

  // Use minimum of 4 decimal places, but preserve more if they exist (up to 8)
  const digitsToShow = Math.max(4, Math.min(decimalPlaces, 8))

  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digitsToShow,
    maximumFractionDigits: digitsToShow,
  })

  return `$${formatter.format(price)}`
}

/**
 * Sums the par value across all orders.
 *
 * @param orders - Array of complete orders
 * @returns Sum of par values from all orders
 */
export function sumParValue(orders: OrderRequest[]): number {
  return orders.reduce((acc, order) => acc + (order.amount?.value ?? 0), 0)
}

export function orderAccountIds(
  order: Pick<OrderRequest, 'account_id' | 'allocations'>,
): string[] {
  const allocations = order.allocations?.account_allocations
  if (allocations) return allocations.map((allocation) => allocation.account_id)
  return order.account_id ? [order.account_id] : []
}

/**
 * Calculates the number of unique accounts across all orders' allocations,
 * counting a single order's account_id when it has no allocations.
 *
 * @param orders - Array of complete orders
 * @returns Count of unique account IDs
 */
export function calculateUniqueAccounts(orders: OrderRequest[]): number {
  return new Set(orders.flatMap(orderAccountIds)).size
}

/**
 * Returns the strictest price from an array of prices based on the order side.
 * For buy orders, returns the minimum price (strictest bid).
 * For sell orders, returns the maximum price (strictest ask).
 *
 * Filters out zero prices as they are never valid for bonds and would skew results.
 *
 * @param side - The order side ('buy' or 'sell')
 * @param prices - Array of price values
 * @returns The strictest price, or null if no valid prices exist
 *
 * @example
 * // Buy side - returns lowest price
 * getStrictestPrice('buy', [102, 101, 103]) // Returns 101
 *
 * @example
 * // Sell side - returns highest price
 * getStrictestPrice('sell', [102, 101, 103]) // Returns 103
 *
 * @example
 * // Filters out zero prices
 * getStrictestPrice('buy', [0, 102, 101]) // Returns 101 (ignores 0)
 */
export function getStrictestPrice(
  side: 'buy' | 'sell',
  prices: number[],
): number | null {
  // Zero is not a valid bond price.
  const validPrices = prices.filter((p) => p !== 0)
  if (validPrices.length === 0) return null
  return side === 'buy' ? Math.min(...validPrices) : Math.max(...validPrices)
}

/**
 * Determines whether an execution price breaches a limit price based on the order side.
 * For buy orders, a breach occurs when the execution price exceeds the limit price
 * (buying for more than the allowed maximum).
 * For sell orders, a breach occurs when the execution price is below the limit price
 * (selling for less than the allowed minimum).
 *
 * @param side - The order side ('buy' or 'sell')
 * @param limitPrice - The limit price constraint
 * @param executionPrice - The actual execution price
 * @returns True if the execution price breaches the limit, false otherwise
 *
 * @example
 * // Buy order - execution price exceeds limit (breach)
 * isPriceBreach('buy', 100, 102) // Returns true (bought at 102 when limit was 100)
 *
 * @example
 * // Buy order - execution price within limit (no breach)
 * isPriceBreach('buy', 100, 99) // Returns false (bought at 99 when limit was 100)
 *
 * @example
 * // Sell order - execution price below limit (breach)
 * isPriceBreach('sell', 100, 98) // Returns true (sold at 98 when limit was 100)
 *
 * @example
 * // Sell order - execution price within limit (no breach)
 * isPriceBreach('sell', 100, 101) // Returns false (sold at 101 when limit was 100)
 */
export function isPriceBreach(
  side: 'buy' | 'sell',
  limitPrice: number,
  executionPrice: number,
): boolean {
  return side === 'buy'
    ? executionPrice > limitPrice
    : executionPrice < limitPrice
}
